import { MenuProps, SelectChangeEvent } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FormHelperText from '@mui/material/FormHelperText';
import { styled } from '@mui/material/styles';
import React from 'react';
import { theme } from '../../../theme/theme';
import { filterScrollbarStyles } from '../../../utils/styles';
import ArrowDownSvg from '../../../assets/img/arrow-down-new.svg';
import WarningSvg from '../../../assets/img/V1/red-warning.svg';

interface IDropdownProps {
  options: Array<{ value: string; label: string }>;
  onChange: (_event: SelectChangeEvent<unknown>) => void;
  value: string | undefined;
  name: string;
  onBlur: (_event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  ariaLabelledBy: string;
  disabled?: boolean;
}

const CustomDropdownIcon = () => (
  <Box
    component="img"
    src={ArrowDownSvg}
    alt="dropdown arrow"
    sx={{
      width: '10px',
      height: '10px',
      marginRight: 1,
    }}
  />
);

const StyledSelect = styled(Select)(({ error, disabled }) => ({
  borderRadius: '8px',
  height: 40,
  overflow: 'hidden',
  backgroundColor: disabled ? theme.palette.shades.whiteBorder2 : 'inherit',
  '& .MuiOutlinedInput-root': {
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&&&:before': {
      borderBottom: 'none',
    },
    '&&:after': {
      borderBottom: 'none',
    },
  },
  '& .MuiSelect-select': {
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    fontSize: '15px',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    lineHeight: '140%',
    '&:focus': {
      borderRadius: '4px',
    },
  },
  '& fieldset': {
    border: `1px solid ${error ? theme.palette.shades.errorBrightMain : theme.palette.shades.whiteBorder2} !important`,
  },
  '&:hover fieldset': {
    border: `1px solid ${error ? theme.palette.shades.errorBrightMain : theme.palette.shades.whiteBorder2} !important`,
  },
  '&.Mui-focused fieldset': {
    border: `1px solid ${error ? theme.palette.shades.errorBrightMain : theme.palette.shades.blueBorder} !important`,
  },
}));
const CustomMenuProps: Partial<MenuProps> = {
  anchorOrigin: {
    vertical: 'bottom',
    horizontal: 'left',
  },
  transformOrigin: {
    vertical: 'top',
    horizontal: 'left',
  },

  PaperProps: {
    sx: {
      ...filterScrollbarStyles,
    },
  },
};

function CustomDropdown({ options, onChange, value, name, onBlur, error, ariaLabelledBy, disabled }: IDropdownProps) {
  return (
    <Box display="flex" flexDirection="column" justifyContent="center" mt={0.5}>
      <FormControl fullWidth>
        <StyledSelect
          id={name}
          aria-labelledby={ariaLabelledBy}
          value={value ?? ''}
          name={name}
          disabled={disabled}
          onChange={onChange}
          inputProps={{ 'aria-label': 'Without label' }}
          displayEmpty
          onBlur={onBlur}
          error={!!error}
          fullWidth
          data-testid={name}
          MenuProps={CustomMenuProps}
          IconComponent={CustomDropdownIcon}
          renderValue={(selected) =>
            selected ? (
              options.find((option) => option.value === selected)?.label
            ) : (
              <Typography variant="tableRowRegular" color={theme.palette.shades.placeHolder}>
                Select
              </Typography>
            )
          }
        >
          {/* <MenuItem value="" disabled>
            <Typography variant="tableRowRegular" color={theme.palette.shades.placeHolder}>
              Select
            </Typography>
          </MenuItem> */}
          {options.map((option) => (
            <MenuItem
              key={option.value}
              value={option.value}
              sx={{ fontWeight: 400, fontSize: '15px', fontFamily: 'Inter', fontStyle: 'normal', lineHeight: '140%' }}
            >
              {option.label.charAt(0).toUpperCase() + option.label.slice(1)}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormControl>
      <FormHelperText
        sx={{
          display: 'flex',
          alignItems: 'center',
          ...theme.typography.footnoteRegular2,
          color: theme.palette.shades.errorBrightMain,
        }}
      >
        {error && (
          <Box component="img" src={WarningSvg} alt="Warning icon" sx={{ width: 16, height: 16, marginRight: 1 }} />
        )}
        {error}
      </FormHelperText>
    </Box>
  );
}

export default CustomDropdown;
